import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation, useRouter } from "@tanstack/react-router";
import { isEnDomainPath } from "@/lib/locale-paths";
import { enArticlePath, trArticlePath } from "@/lib/article-pairs";
import { getCop31Note, cop31NotePath } from "@/lib/cop31-notes";
import { getPublicCopy } from "@/lib/public-copy.functions";

// [trPath, enPath, rootOnly]
// rootOnly pairs have no slug-level equivalence: the Turkish archive slugs
// (/yazilar/<tr-slug>) do not exist under /ideas, so a sub-path must land on
// the English section root instead of a non-existent detail page. Articles that
// DO have a translated counterpart are resolved slug-by-slug via ARTICLE_PAIRS
// before these prefix rules run.
const LANG_PATH_PAIRS: Array<[string, string, boolean?]> = [
  ["/cop31/notlar", "/cop31-guide/notes"],
  ["/cop31", "/cop31-guide"],
  ["/yazilar", "/ideas", true],
  ["/arsiv", "/ideas", true],
  ["/yapay-zeka-toplum", "/ai-society", true],
  ["/iklim-sistemleri", "/climate-systems", true],
  ["/insan-ve-gelecek", "/human-futures", true],
  ["/surdurulebilirlik", "/sustainability-critique", true],
  ["/raporlar", "/research", true],
  ["/sozluk", "/concepts", true],
  ["/etiket", "/topics", true],
];

function mapPathForLang(pathname: string, target: Lang): string {
  if (target === "en") {
    // Root maps to the EN publication landing (the English domain root).
    if (pathname === "/" || pathname === "") return "/";
    // Article with a real English translation → its own URL, not the section root.
    const article = enArticlePath(pathname);
    if (article) return article;
    if (pathname.startsWith("/cop31/notlar/")) {
      const note = getCop31Note(pathname.slice("/cop31/notlar/".length));
      return note ? cop31NotePath(note.slug, "en") : "/cop31-guide";
    }
    for (const [tr, en, rootOnly] of LANG_PATH_PAIRS) {
      if (pathname === tr) return en;
      if (pathname.startsWith(tr + "/")) {
        return rootOnly ? en : en + pathname.slice(tr.length);
      }
    }
    // TR-only pages without an EN counterpart fall back to the EN landing.
    if (!isEnDomainPath(pathname)) return "/";
    return pathname;
  }
  // target === "tr"
  const trArticle = trArticlePath(pathname);
  if (trArticle) return trArticle;
  // EN-only publication pages (ideas, concepts, research, frameworks, ...) have
  // no Turkish counterpart → Turkish home.
  if (isEnDomainPath(pathname)) {
    for (const [tr, en] of LANG_PATH_PAIRS) {
      if (pathname === en) return tr;
      if (pathname.startsWith(en + "/") && en === "/cop31-guide/notes") {
        const slug = pathname.slice(en.length + 1);
        const note = getCop31Note(slug);
        // Notes published only in English have no Turkish permalink.
        return note && note.locale !== "en" ? cop31NotePath(note.slug, "tr") : "/cop31";
      }
    }
    return "/";
  }

  for (const [tr, en, rootOnly] of LANG_PATH_PAIRS) {
    if (pathname === en) return tr;
    if (pathname.startsWith(en + "/")) {
      return rootOnly ? tr : tr + pathname.slice(en.length);
    }
  }
  return pathname;
}



export type Lang = "en" | "tr";

type Dict = Record<string, { en: string; tr: string }>;

export const dict = {
  // Header / nav
  "nav.ai": { en: "AI & Society", tr: "Yapay Zekâ & Toplum" },
  "nav.climate": { en: "Climate & Systems", tr: "İklim & Sistemler" },
  "nav.human": { en: "Human Futures", tr: "İnsan ve Gelecek" },
  "nav.sustainability": { en: "Sustainability", tr: "Sürdürülebilirlik" },
  "nav.about": { en: "About", tr: "Hakkımda" },
  "nav.author": { en: "Author", tr: "Yazar" },
  "nav.subscribe": { en: "Sign Up", tr: "Üye Ol" },

  // Footer
  "footer.rights": {
    en: "Smart Sustainability",
    tr: "Akıllı Sürdürülebilirlik",
  },
  "footer.archive": { en: "Archive", tr: "Arşiv" },
  "footer.privacy": { en: "Privacy", tr: "Gizlilik" },
  "newsletter.consent": {
    en: "I agree to receive the newsletter by email. I can unsubscribe at any time. See the",
    tr: "Bülteni e-posta ile almayı kabul ediyorum. İstediğim zaman abonelikten çıkabilirim. Detaylar için",
  },
  "newsletter.privacy_link": { en: "privacy policy", tr: "gizlilik politikası" },

  // Page titles
  "title.home": {
    en: "Smart Sustainability — Essays on AI, climate & human futures",
    tr: "Akıllı Sürdürülebilirlik — Yapay zekâ, iklim ve insan gelecekleri",
  },
  "title.archive": { en: "Archive — Smart Sustainability", tr: "Arşiv — Akıllı Sürdürülebilirlik" },
  "title.about": { en: "About — Smart Sustainability", tr: "Hakkımda — Akıllı Sürdürülebilirlik" },
  "title.book": {
    en: "Artificial Intelligence: The Two Faces of Power — Arda Öztaşkın",
    tr: "Yapay Zekâ: Gücün İki Yüzü — Arda Öztaşkın",
  },
  "title.ai": { en: "AI & Society — Smart Sustainability", tr: "Yapay Zekâ & Toplum — Akıllı Sürdürülebilirlik" },
  "title.climate": { en: "Climate & Systems — Smart Sustainability", tr: "İklim & Sistemler — Akıllı Sürdürülebilirlik" },
  "title.human": { en: "Human Futures — Smart Sustainability", tr: "İnsan ve Gelecek — Akıllı Sürdürülebilirlik" },
  "title.sustain": { en: "Sustainability Critique — Smart Sustainability", tr: "Sürdürülebilirlik Eleştirisi — Akıllı Sürdürülebilirlik" },
  "meta.home": {
    en: "A personal archive of essays on artificial intelligence, climate systems, sustainability critique and the futures we build together.",
    tr: "Yapay zekâ, iklim sistemleri, sürdürülebilirlik eleştirisi ve insan gelecekleri üzerine kişisel yazı arşivi.",
  },
  "meta.archive": {
    en: "Full archive of essays on AI, climate, sustainability and human futures.",
    tr: "Yapay zekâ, iklim, sürdürülebilirlik ve insan gelecekleri üzerine tüm yazı arşivi.",
  },
  "meta.about": {
    en: "About Arda Öztaşkın — communications executive, author and sustainability advocate.",
    tr: "Arda Öztaşkın hakkında — iletişim yöneticisi, yazar ve sürdürülebilirlik savunucusu.",
  },
  "meta.book": {
    en: "Humanity at the AI crossroads: opportunity or threat? About Arda Öztaşkın's book.",
    tr: "İnsanlık yapay zekâ virajında: Fırsat mı, tehdit mi? Arda Öztaşkın'ın kitabı üzerine.",
  },
  "meta.ai": {
    en: "Essays on artificial intelligence, ethics, trust and the systems reshaping society.",
    tr: "Yapay zekâ, etik, güven ve toplumu yeniden şekillendiren sistemler üzerine yazılar.",
  },
  "meta.climate": {
    en: "Essays on climate, energy and the systems that hold the biosphere together.",
    tr: "İklim, enerji ve biyosferi bir arada tutan sistemler üzerine yazılar.",
  },
  "meta.human": {
    en: "Essays on the futures we choose, inherit and abandon.",
    tr: "Seçtiğimiz, devraldığımız ve terk ettiğimiz gelecekler üzerine yazılar.",
  },
  "meta.sustain": {
    en: "Critical essays on greenwashing, ESG and the limits of sustainability discourse.",
    tr: "Greenwashing, ESG ve sürdürülebilirlik söyleminin sınırları üzerine eleştirel yazılar.",
  },

  // Index
  "index.hero_title": { en: "Smart Sustainability — essays on AI, climate and human futures", tr: "Akıllı Sürdürülebilirlik — yapay zekâ, iklim ve insan geleceği üzerine denemeler" },
  "index.hero_subtitle": { en: "Essays by Arda Öztaşkın on the systems shaping our shared future.", tr: "Arda Öztaşkın'ın ortak geleceğimizi şekillendiren sistemler üzerine denemeleri." },
  "index.read_essay": { en: "Read Essay", tr: "Yazıyı Oku" },
  "index.read_min": { en: "read", tr: "okuma" },
  "index.latest": { en: "Latest Thinking", tr: "Son Yazılar" },
  "index.view_archive": { en: "View archive →", tr: "Arşivi gör →" },
  "common.translating": { en: "Translating…", tr: "Çevriliyor…" },
  "common.share": { en: "Share", tr: "Paylaş" },
  "common.share_x": { en: "Share on X", tr: "X'te paylaş" },
  "common.share_linkedin": { en: "Share on LinkedIn", tr: "LinkedIn'de paylaş" },
  "common.author_portrait": { en: "Portrait of Arda Öztaşkın, author and sustainability advocate", tr: "Yazar ve sürdürülebilirlik savunucusu Arda Öztaşkın'ın portresi" },
  "common.book_cover": {
    en: "Artificial Intelligence: The Two Faces of Power book cover",
    tr: "Yapay Zekâ: Gücün İki Yüzü kitap kapağı",
  },

  "index.ekonomim.tag": { en: "Column · Ekonomim", tr: "Köşe · Ekonomim" },
  "index.ekonomim.title_a": { en: "My columns at", tr: "Ekonomim gazetesindeki" },
  "index.ekonomim.title_b": { en: "Ekonomim", tr: "köşe yazıları" },
  "index.ekonomim.body": {
    en: "Regular columns on sustainability, artificial intelligence, corporate communication and the transformation of business — all available on my author page at Ekonomim.",
    tr: "Sürdürülebilirlik, yapay zekâ, kurumsal iletişim ve iş dünyasının dönüşümü üzerine düzenli olarak yayımlanan yazıların tamamına Ekonomim'deki yazar sayfasından ulaşabilirsiniz.",
  },
  "index.ekonomim.cta": { en: "Read the columns", tr: "Yazıları oku" },

  "index.bio.role": {
    en: "Communications Executive · Author",
    tr: "İletişim Yöneticisi · Yazar",
  },
  "index.bio.body": {
    en: "I write at the intersection of corporate leadership, academia, and journalism — advancing sustainable transformation through strategic communication and systems thinking.",
    tr: "Kurumsal liderlik, akademi ve gazetecilik kesişiminde yazıyorum — stratejik iletişim ve sistem düşüncesiyle sürdürülebilir dönüşümü ileriye taşıyorum.",
  },
  "index.bio.more": { en: "More about the work", tr: "Çalışmalarım hakkında" },

  "index.books.heading": { en: "Books", tr: "Kitaplar" },
  "index.book.badge": { en: "Book", tr: "Kitap" },
  "index.book.title_a": { en: "Artificial Intelligence:", tr: "Yapay Zekâ:" },
  "index.book.title_b": { en: "The Two Faces of Power", tr: "Gücün İki Yüzü" },
  "index.book.body": {
    en: "Exploring humanity's relationship with technology — a look at how algorithmic governance is restructuring politics, labor and the very concept of human agency.",
    tr: "İnsanlığın teknolojiyle ilişkisini sorgulayan bir kitap — algoritmik yönetimin siyaseti, emeği ve insan failliğini nasıl yeniden şekillendirdiğine dair.",
  },
  "index.book.cta": { en: "Read the AI book", tr: "Yapay zekâ kitabını incele" },
  "index.book2.badge": { en: "Book", tr: "Kitap" },
  "index.book2.title": { en: "Not a Climate Crisis — A Crisis of Humanity", tr: "İklim değil, insanlık krizi" },
  "index.book2.body": {
    en: "Today's sustainability questions — climate, technology, economy, society — explored through one frame: humanity itself. With forewords by Yekta Kopan and Prof. Dr. Selçuk Şirin.",
    tr: "İklim, teknoloji, ekonomi ve toplum… Günümüzün sürdürülebilirlik meselelerini tek bir eksende, insanlığın kendisinde okuyan bir kitap. Yekta Kopan ve Prof. Dr. Selçuk Şirin'in sunuşuyla.",
  },
  "index.book2.cta": { en: "Read the climate book", tr: "İklim krizi kitabını incele" },

  // Newsletter
  "newsletter.title": { en: "The Dispatch", tr: "Bülten" },
  "newsletter.body": {
    en: "Bi-weekly synthesis of emerging tech and systemic ecology. No noise, just signal.",
    tr: "Yeni teknolojiler ve sistemik ekoloji üzerine iki haftada bir sentez. Gürültü yok, sadece sinyal.",
  },
  "newsletter.email": { en: "Email address", tr: "E-posta adresi" },
  "newsletter.cta": { en: "Sign Up", tr: "Üye Ol" },
  "newsletter.success": {
    en: "You're in. Thanks for joining.",
    tr: "Aramızdasınız. Katıldığınız için teşekkürler.",
  },
  "newsletter.error": {
    en: "Couldn't subscribe. Please try again.",
    tr: "Abone olunamadı. Lütfen tekrar deneyin.",
  },
  "newsletter.sending": { en: "Sending…", tr: "Gönderiliyor…" },
  "newsletter.already": {
    en: "This email is already subscribed.",
    tr: "Bu e-posta adresi zaten kayıtlı.",
  },
  "newsletter.inline_title": {
    en: "This kind of writing, every two weeks.",
    tr: "Bu tür yazılar, iki haftada bir.",
  },
  "newsletter.inline_body": {
    en: "One long thought on AI, climate and systems — straight to your inbox. No noise, just signal.",
    tr: "Yapay zekâ, iklim ve sistemler üzerine tek bir uzun düşünce — doğrudan gelen kutunuza. Gürültü yok, sadece sinyal.",
  },
  "newsletter.inline_footer": {
    en: "You can unsubscribe anytime.",
    tr: "İstediğiniz an çıkabilirsiniz.",
  },

  // Categories
  "cat.label": { en: "Category", tr: "Kategori" },
  "cat.empty": { en: "No essays in this category yet.", tr: "Bu kategoride henüz yazı yok." },
  "cat.back": { en: "← Back to all essays", tr: "← Tüm yazılara dön" },

  "cat.ai.title": { en: "AI & Society", tr: "Yapay Zekâ & Toplum" },
  "cat.ai.tagline": {
    en: "Essays on artificial intelligence, ethics, trust and the systems quietly redrawing the social contract.",
    tr: "Yapay zekâ, etik, güven ve sosyal sözleşmeyi sessizce yeniden çizen sistemler üzerine yazılar.",
  },
  "cat.climate.title": { en: "Climate & Systems", tr: "İklim & Sistemler" },
  "cat.climate.tagline": {
    en: "Essays on planetary boundaries, energy transitions and the contradictions of the green economy.",
    tr: "Gezegen sınırları, enerji dönüşümü ve yeşil ekonominin çelişkileri üzerine yazılar.",
  },
  "cat.human.title": { en: "Human Futures", tr: "İnsan ve Gelecek" },
  "cat.human.tagline": {
    en: "Essays on consciousness, work, memory and what remains of the human in the shadow of code.",
    tr: "Bilinç, emek, bellek ve kodun gölgesinde insandan geriye kalan üzerine yazılar.",
  },
  "cat.sustain.title": { en: "Sustainability Critique", tr: "Sürdürülebilirlik Eleştirisi" },
  "cat.sustain.tagline": {
    en: "Critical essays on greenwashing, ESG and the limits of sustainability discourse.",
    tr: "Greenwashing, ESG ve sürdürülebilirlik söyleminin sınırları üzerine eleştirel yazılar.",
  },

  // Archive
  "archive.kicker": { en: "Archive", tr: "Arşiv" },
  "archive.title": { en: "All essays", tr: "Tüm yazılar" },
  "archive.body": {
    en: "Every piece from the journal, in reverse chronological order.",
    tr: "Dergiden her yazı, tersten kronolojik sırayla.",
  },
  "archive.count_essays": { en: "essays", tr: "yazı" },
  "archive.translating_note": {
    en: "Translating archive…",
    tr: "Arşiv çevriliyor…",
  },
  "archive.filter_all": { en: "All", tr: "Tümü" },
  "archive.search_placeholder": { en: "Search the archive…", tr: "Arşivde ara…" },
  "archive.no_matches": { en: "No essays match your filters.", tr: "Filtrelerinizle eşleşen yazı yok." },
  "archive.showing": { en: "Showing", tr: "Gösterilen" },
  "archive.of": { en: "of", tr: "/" },


  // Essay page
  "essay.not_found": { en: "Essay not found", tr: "Yazı bulunamadı" },
  "essay.browse": { en: "Browse the archive", tr: "Arşive göz at" },
  "essay.load_err": { en: "Couldn't load essay", tr: "Yazı yüklenemedi" },
  "essay.home": { en: "Back home", tr: "Ana sayfaya dön" },
  "essay.all": { en: "← All essays", tr: "← Tüm yazılar" },
  
  "essay.related": { en: "Related reading", tr: "İlgili yazılar" },
  "essay.tldr": { en: "TL;DR", tr: "Özet" },

  // Book page
  "book.kicker": { en: "Book · 2nd Edition", tr: "Kitap · 2. Baskı" },
  "book.title_a": { en: "Artificial Intelligence:", tr: "Yapay Zekâ:" },
  "book.title_b": { en: "The Two Faces of Power", tr: "Gücün İki Yüzü" },
  "book.subtitle": {
    en: "Humanity at the AI crossroads: opportunity or threat?",
    tr: "İnsanlık yapay zekâ virajında: Fırsat mı, tehdit mi?",
  },
  "book.p1": {
    en: "Artificial intelligence is no longer a forecast about the future — it is the reality of today. It is quietly embedding itself into our decisions, our work, public life and our private lives. So in whose hands is this power accumulating, and on whose behalf is it speaking?",
    tr: "Yapay zekâ artık bir gelecek tahmini değil — bugünün gerçeği. Karar mekanizmalarımıza, çalışma biçimlerimize, kamusal alana ve özel hayatlarımıza sessizce yerleşiyor. Peki bu güç kimin elinde birikiyor, kimin adına konuşuyor?",
  },
  "book.p2": {
    en: "The Two Faces of Power holds AI's liberating promise and its face of control and inequality within a single frame. It explores how algorithms reshape politics, transform labor, and push the boundaries of human agency.",
    tr: "Gücün İki Yüzü, yapay zekânın özgürleştirici vaatleriyle denetim ve eşitsizlik üreten yüzünü aynı çerçevede tutuyor. Algoritmaların siyaseti nasıl yeniden şekillendirdiğini, emeği nasıl dönüştürdüğünü ve insan failliğinin sınırlarını nereye doğru ittiğini tartışıyor.",
  },
  "book.p3": {
    en: "The book puts the tensions between Big Tech's agenda, state regulation, data privacy, automation and ethics on the table in plain language. The goal is not to offer ready answers — it is to ask the right questions.",
    tr: "Kitap; teknoloji şirketlerinin gündemi, devletlerin düzenleme çabaları, veri mahremiyeti, otomasyon ve etik arasındaki gerilimleri yalın bir dille okurun önüne koyuyor. Hedef, hazır cevaplar sunmak değil — doğru soruları gündeme getirmek.",
  },
  "book.publisher": { en: "Publisher", tr: "Yayınevi" },
  "book.buy": { en: "Buy →", tr: "Satın Al →" },
  "book.home": { en: "← Home", tr: "← Ana sayfa" },

  // About page
  "about.kicker": { en: "About", tr: "Hakkımda" },
  "about.title_a": { en: "Rethinking AI,", tr: "Yapay Zekâyı," },
  "about.title_b": { en: "Climate & Systems", tr: "İklimi & Sistemleri Yeniden Düşünmek" },

  "about.p1_a": { en: "Smart Sustainability", tr: "Akıllı Sürdürülebilirlik" },
  "about.p1_b": {
    en: " is not a content platform. It is a space for ",
    tr: " bir içerik platformu değildir. ",
  },
  "about.p1_c": {
    en: "critical thinking at the intersection of artificial intelligence, climate change, and sustainability",
    tr: "yapay zekâ, iklim değişikliği ve sürdürülebilirliğin kesiştiği noktada eleştirel düşünme alanıdır",
  },
  "about.p1_d": { en: ".", tr: "." },

  "about.p2_a": {
    en: "It was created with a simple but uncomfortable premise: ",
    tr: "Basit ama rahatsız edici bir önermeyle kuruldu: ",
  },
  "about.p2_b": {
    en: "The crises we face are not isolated. They are systemic.",
    tr: "Karşı karşıya olduğumuz krizler izole değil. Sistemiktir.",
  },

  "about.p3": {
    en: "Climate is not only an environmental issue. AI is not only a technological development. Sustainability is not only a corporate agenda.",
    tr: "İklim yalnızca bir çevre meselesi değildir. Yapay zekâ yalnızca bir teknolojik gelişme değildir. Sürdürülebilirlik yalnızca bir kurumsal gündem değildir.",
  },
  "about.p4": {
    en: "They are all expressions of how we design, optimize, and justify the systems we live in.",
    tr: "Hepsi, içinde yaşadığımız sistemleri nasıl tasarladığımızın, optimize ettiğimizin ve meşrulaştırdığımızın ifadesidir.",
  },

  "about.h2_stands": { en: "What This Platform Stands For", tr: "Bu Platform Neyi Savunuyor" },
  "about.stands_intro": {
    en: "Smart Sustainability explores the deeper structures behind visible problems:",
    tr: "Akıllı Sürdürülebilirlik, görünür sorunların ardındaki derin yapıları araştırır:",
  },
  "about.bullet_1": {
    en: "How AI reshapes power, labor, and decision-making.",
    tr: "Yapay zekânın gücü, emeği ve karar almayı nasıl yeniden şekillendirdiği.",
  },
  "about.bullet_2": {
    en: "Why sustainability often turns into narrative rather than transformation.",
    tr: "Sürdürülebilirliğin neden dönüşüm yerine sıklıkla bir anlatıya dönüştüğü.",
  },
  "about.bullet_3": {
    en: "How economic systems reproduce the very crises they claim to solve.",
    tr: "Ekonomik sistemlerin çözdüğünü iddia ettiği krizleri nasıl yeniden ürettiği.",
  },
  "about.bullet_4": {
    en: "What it means to remain human in an increasingly optimized world.",
    tr: "Giderek daha fazla optimize edilen bir dünyada insan kalmanın ne anlama geldiği.",
  },
  "about.stands_p1_a": { en: "This is not about reacting to trends. It is about ", tr: "Bu, trendlere tepki vermekle ilgili değildir. " },
  "about.stands_p1_b": {
    en: "understanding the systems that produce them",
    tr: "onları üreten sistemleri anlamakla ilgilidir",
  },
  "about.stands_p1_c": { en: ".", tr: "." },
  "about.stands_p2_a": {
    en: "In a landscape dominated by simplified narratives, fast content, and performative transformation, this platform takes a different path: ",
    tr: "Basitleştirilmiş anlatıların, hızlı içeriğin ve performatif dönüşümün hâkim olduğu bir manzarada bu platform farklı bir yol izler: ",
  },
  "about.stands_p2_b": {
    en: "It slows down. It questions. It challenges.",
    tr: "Yavaşlar. Sorgular. Meydan okur.",
  },
  "about.stands_p3": {
    en: "Not to reject progress — but to ask what kind of progress we are actually building.",
    tr: "İlerlemeyi reddetmek için değil — gerçekte ne tür bir ilerleme inşa ettiğimizi sormak için.",
  },

  "about.h2_author": { en: "About the Author", tr: "Yazar Hakkında" },
  "about.role": {
    en: "Communications Executive · Author · Sustainability Advocate",
    tr: "İletişim Yöneticisi · Yazar · Sürdürülebilirlik Savunucusu",
  },
  "about.bio_1": {
    en: "Born in Ankara, Turkey. He graduated from TED Ankara College and earned his degree from the Faculty of Political Science at Ankara University — widely regarded as the cradle of Turkey's public and political leadership.",
    tr: "Ankara doğumlu. TED Ankara Koleji'nden mezun oldu ve Türkiye'nin kamu ve siyasi liderliğinin beşiği sayılan Ankara Üniversitesi Siyasal Bilgiler Fakültesi'nde lisans eğitimini tamamladı.",
  },
  "about.bio_2": {
    en: "He began his professional career in the advertising and public relations department of Demirbank, one of Turkey's leading financial institutions of its time. He later held key communication roles at Dışbank and Fortis Bank, contributing to brand positioning and managing communication through major corporate transitions. In 2008, he joined Yapı Kredi, one of the country's most prominent banks, as Director of Brand Management. Since 2018, he has led Yapı Kredi's Corporate Communications department, overseeing brand strategy, stakeholder engagement, media relations, reputation management, and sustainability communication.",
    tr: "Profesyonel kariyerine, döneminin önde gelen finans kuruluşlarından Demirbank'ın reklam ve halkla ilişkiler bölümünde başladı. Ardından Dışbank ve Fortis Bank'ta üst düzey iletişim görevlerinde bulunarak marka konumlandırmasına katkıda bulundu ve büyük kurumsal geçiş süreçlerinde iletişimi yönetti. 2008 yılında, ülkenin en köklü bankalarından Yapı Kredi'ye Marka Yönetimi Direktörü olarak katıldı. 2018'den bu yana Yapı Kredi'nin Kurumsal İletişim bölümünü yönetiyor; marka stratejisi, paydaş ilişkileri, medya ilişkileri, itibar yönetimi ve sürdürülebilirlik iletişimini yürütüyor.",
  },
  "about.bio_3": {
    en: "He currently serves as Vice President of the Corporate Communicators Association (Kurumsal İletişimciler Derneği – KİD). He is also a member of the Advisory Boards of Bilgi University's Faculty of Communication and Sustainable Brands Turkey. Since 2023, he has been teaching Sustainability Communication at Bahçeşehir University.",
    tr: "Hâlihazırda Kurumsal İletişimciler Derneği'nin (KİD) Başkan Yardımcılığını yürütüyor. Aynı zamanda Bilgi Üniversitesi İletişim Fakültesi ve Sustainable Brands Türkiye Danışma Kurulları üyesidir. 2023'ten bu yana Bahçeşehir Üniversitesi'nde Sürdürülebilirlik İletişimi dersi veriyor.",
  },
  "about.bio_4": {
    en: "He is a weekly columnist for Ekonomi Gazetesi (Ekonomim). His column \"Akıllı Sürdürülebilirlik\" (Smart Sustainability) examines the intersections of sustainability, technology, communication, and ethics.",
    tr: "Ekonomi Gazetesi'nde (Ekonomim) haftalık köşe yazarıdır. \"Akıllı Sürdürülebilirlik\" başlıklı köşesinde sürdürülebilirlik, teknoloji, iletişim ve etiğin kesişimlerini ele alır.",
  },
  "about.books_intro": { en: "He is the author of three books:", tr: "Üç kitabın yazarıdır:" },
  "about.book_1": {
    en: "\"It's Not a Climate Crisis, It's a Crisis of Humanity\" — reframes the environmental discourse around justice, ethics, and collective responsibility.",
    tr: "\"İklim Değil, İnsanlık Krizi\" — çevre söylemini adalet, etik ve kolektif sorumluluk etrafında yeniden çerçeveler.",
  },
  "about.book_2": {
    en: "\"Artificial Intelligence: The Two Faces of Power\" — examines how AI reshapes power structures, its impact on labor and data privacy, and the future of human agency.",
    tr: "\"Yapay Zekâ: Gücün İki Yüzü\" — yapay zekânın güç yapılarını nasıl yeniden biçimlendirdiğini, emek ve veri mahremiyetine etkisini ve insan failliğinin geleceğini inceler.",
  },
  "about.book_3": {
    en: "\"Loneliness Inc.\" — argues that modern loneliness is not a personal failure but a predictable, designed, and highly profitable outcome of how our world is built, tracing the rise of the loneliness economy across markets, communities, and policy.",
    tr: "\"Yalnızlık A.Ş.\" — modern yalnızlığın kişisel bir başarısızlık değil, içinde yaşadığımız dünyanın kuruluş biçiminin öngörülebilir, tasarlanmış ve son derece kârlı bir sonucu olduğunu öne sürer; yalnızlık ekonomisinin piyasalar, topluluklar ve kamu politikası ekseninde yükselişini izler.",
  },
  "about.bio_5": {
    en: "His work operates at the crossroads of corporate leadership, academia, and journalism. His mission is to advance sustainable transformation through strategic communication and systems thinking.",
    tr: "Çalışmaları kurumsal liderlik, akademi ve gazeteciliğin kesişiminde yer alır. Misyonu, stratejik iletişim ve sistem düşüncesi aracılığıyla sürdürülebilir dönüşümü ileri taşımaktır.",
  },
  "about.contact": { en: "Contact:", tr: "İletişim:" },
  "about.talks_kicker": { en: "Talks & Conversations", tr: "Konuşmalar & Söyleşiler" },
  "about.talks_title": {
    en: "Recordings of conferences, panels and conversations.",
    tr: "Konferans, panel ve söyleşi kayıtları.",
  },
  "about.talks_body": {
    en: "Keynotes, panels and workshops on AI, sustainability and the future of human agency.",
    tr: "Yapay zekâ, sürdürülebilirlik ve insan failliğinin geleceği üzerine açılış konuşmaları, paneller ve atölyeler.",
  },
  "about.talks_cta": { en: "See talks", tr: "Konuşmaları gör" },

  // 404 / error
  "err.404_title": { en: "Page not found", tr: "Sayfa bulunamadı" },
  "err.404_body": {
    en: "The page you're looking for doesn't exist or has been moved.",
    tr: "Aradığınız sayfa mevcut değil veya taşınmış.",
  },
  "err.home": { en: "Return home", tr: "Ana sayfaya dön" },
  "err.title": { en: "This page didn't load", tr: "Bu sayfa yüklenemedi" },
  "err.body": {
    en: "Something went wrong. Try refreshing or head back home.",
    tr: "Bir şeyler ters gitti. Sayfayı yenilemeyi deneyin veya ana sayfaya dönün.",
  },
  "err.retry": { en: "Try again", tr: "Tekrar dene" },
  "err.go_home": { en: "Go home", tr: "Ana sayfa" },

  // Search
  "nav.search": { en: "Search", tr: "Ara" },
  "search.placeholder": { en: "Search essays…", tr: "Yazılarda ara…" },
  "search.title": { en: "Search", tr: "Arama" },
  "search.results_for": { en: "Results for", tr: "Arama sonuçları:" },
  "search.no_results": { en: "No essays match your search.", tr: "Aramanızla eşleşen yazı bulunamadı." },
  "search.empty": { en: "Type a keyword to search the archive.", tr: "Arşivde aramak için bir kelime yazın." },
  "search.count": { en: "results", tr: "sonuç" },
  "search.filters": { en: "Filters", tr: "Filtreler" },
  "search.filter_category": { en: "Category", tr: "Tema" },
  "search.filter_year": { en: "Year", tr: "Yıl" },
  "search.filter_sort": { en: "Sort", tr: "Sırala" },
  "search.filter_all": { en: "All", tr: "Tümü" },
  "search.suggested": { en: "Suggested keywords", tr: "Önerilen anahtar kelimeler" },
  "search.clear": { en: "Clear filters", tr: "Filtreleri temizle" },
  "search.sort_newest": { en: "Newest", tr: "En yeni" },
  "search.sort_oldest": { en: "Oldest", tr: "En eski" },
  "search.sort_relevance": { en: "Relevance", tr: "Uygunluk" },
  "title.search": { en: "Search — Smart Sustainability", tr: "Arama — Akıllı Sürdürülebilirlik" },
  "meta.search": {
    en: "Search the archive of essays on AI, climate, sustainability and human futures.",
    tr: "Yapay zekâ, iklim, sürdürülebilirlik ve insan gelecekleri üzerine yazı arşivinde arama yapın.",
  },
} satisfies Dict;

export type TKey = keyof typeof dict;

interface LangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TKey) => string;
}

const Ctx = createContext<LangCtx | null>(null);

const STORAGE_KEY = "lang";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function writeLangCookie(l: Lang) {
  document.cookie = `${STORAGE_KEY}=${l}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function readStoredLang(): Lang {
  if (typeof window === "undefined") return "tr";
  // Host is the source of truth: EN domain always renders EN, TR domain
  // always renders TR. localStorage is per-origin but users still land on
  // the wrong tab title after crossing domains — never let a previous
  // session's stored lang override the current host.
  const host = window.location.hostname.toLowerCase();
  const isEnHost = host === "smartsustainability.ai" || host === "www.smartsustainability.ai";
  const isTrHost = host === "akillisurdurulebilirlik.com" || host === "www.akillisurdurulebilirlik.com";
  if (isEnHost) {
    try { localStorage.setItem(STORAGE_KEY, "en"); writeLangCookie("en"); } catch { /* ignore */ }
    return "en";
  }
  if (isTrHost) {
    try { localStorage.setItem(STORAGE_KEY, "tr"); writeLangCookie("tr"); } catch { /* ignore */ }
    return "tr";
  }
  // Preview / lovable.app hosts: fall back to pathname, then storage.
  const path = window.location.pathname;
  if (isEnDomainPath(path)) return "en";
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Lang | null;
    if (stored === "en" || stored === "tr") {
      writeLangCookie(stored);
      return stored;
    }
  } catch {
    /* ignore */
  }
  writeLangCookie("tr");
  return "tr";
}


function applyLang(l: Lang, pathname = "/") {
  if (typeof document === "undefined") return;
  document.documentElement.lang = l;
  // Titles and descriptions come only from each route's head(); never overwrite them here.
  void pathname;
}

export function LangProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  // The URL is the only signal available during SSR, so English section paths render English
  // markup (and English internal links) on the server too. Host/localStorage
  // refinement happens after hydration.
  const initialLang: Lang = isEnDomainPath(location.pathname) ? "en" : "tr";
  const [lang, setLangState] = useState<Lang>(initialLang);
  const [overrides, setOverrides] = useState<Record<string, { en: string; tr: string }>>({});
  const router = useRouter();

  useEffect(() => {
    const stored = readStoredLang();
    if (stored !== "tr") setLangState(stored);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      const data = await getPublicCopy();
      if (cancelled || !data) return;
      const next: Record<string, { en: string; tr: string }> = {};
      for (const row of data as Array<{ key: string; value_en: string; value_tr: string }>) {
        next[row.key] = { en: row.value_en, tr: row.value_tr };
      }
      setOverrides(next);
    };

    // Defer non-critical content overrides until after LCP/idle.
    const w = typeof window !== "undefined" ? (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }) : undefined;
    const id = w?.requestIdleCallback
      ? w.requestIdleCallback(run, { timeout: 3000 })
      : (setTimeout(run, 1500) as unknown as number);
    return () => {
      cancelled = true;
      const cancelIdle = (w as unknown as { cancelIdleCallback?: (id: number) => void } | undefined)?.cancelIdleCallback;
      if (cancelIdle) cancelIdle(id);
      else clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
    };
  }, []);


  useEffect(() => {
    applyLang(lang, location.pathname);
  }, [lang, location.pathname]);

  const setLang = (l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
      writeLangCookie(l);
    } catch {
      /* ignore */
    }
    const current = location.pathname;
    const mapped = mapPathForLang(current, l);
    // Cross-domain switch: EN lives on smartsustainability.ai (served at
    // root, no /en prefix on that host); TR lives on
    // akillisurdurulebilirlik.com. Force full navigation so the user lands
    // on the correct primary host with the correct canonical.
    if (typeof window !== "undefined") {
      const host = window.location.hostname.toLowerCase();
      const isEnHost = host === "smartsustainability.ai" || host === "www.smartsustainability.ai";
      const isTrHost = host === "akillisurdurulebilirlik.com" || host === "www.akillisurdurulebilirlik.com";
      const search = window.location.search ?? "";
      if (l === "en" && !isEnHost) {
        window.location.href = `https://smartsustainability.ai${mapped}${search}`;
        return;
      }
      if (l === "tr" && !isTrHost) {
        window.location.href = `https://akillisurdurulebilirlik.com${mapped}${search}`;
        return;
      }
    }
    if (mapped !== current) {
      router.navigate({ to: mapped, search: location.search as never });
    }
  };


  const t = (key: TKey) => {
    const override = overrides[key as string];
    if (override) {
      const v = override[lang];
      if (v && v.trim()) return v;
    }
    return dict[key][lang];
  };

  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export function useT() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Fallback for SSR or if used outside provider — return English
    return {
      lang: "en" as Lang,
      setLang: () => {},
      t: (key: TKey) => dict[key].en,
    };
  }
  return ctx;
}
